import { useState } from "react"
import { Link, createFileRoute } from "@tanstack/react-router"
import { toast } from "sonner"
import type { IddModulInhalt } from "@nl/shared"

import { IddInhaltDialog } from "@/components/admin/idd-inhalt-dialog"
import { formatDatum } from "@/components/idd/idd-format"
import { IDD_ART_TITEL, IDD_MODULE, iddInhaltVon } from "@/components/idd/idd-module"
import { LoadState } from "@/components/load-state"
import { PageHeader } from "@/components/page-header"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { apiFehlerDetail } from "@/lib/api-client"
import { versionedImageUrl } from "@/lib/image-version"
import { useIddInhaltZuruecksetzen, useIddInhalte } from "@/lib/idd-queries"

function AdminIddInhalte() {
  const { data, isError, refetch } = useIddInhalte()
  const zuruecksetzen = useIddInhaltZuruecksetzen()
  const [offen, setOffen] = useState<{ titel: string; inhalt: IddModulInhalt }>()

  const standard = async (inhalt: IddModulInhalt) => {
    try {
      await zuruecksetzen.mutateAsync(inhalt.art, inhalt.modulKey)
      toast.success("Auf die Standardtexte zurückgesetzt.")
    } catch (error) {
      toast.error(apiFehlerDetail(error) ?? "Das Zurücksetzen hat nicht geklappt.")
    }
  }

  return (
    <div className="space-y-7">
      <PageHeader
        title="IDD-Inhalte"
        description="Beschreibung, Lernziele, Lerninhalte, Kompetenzen, Zielgruppe und Bild der Trainings im IDD-Bereich. Gilt für alle Sparkassen."
      />
      {!data ? (
        <LoadState failed={isError} onRetry={() => void refetch()}>
          Die IDD-Inhalte konnten nicht geladen werden.
        </LoadState>
      ) : (
        <ul className="divide-y divide-[var(--rule)] rounded-md bg-card ring-1 ring-foreground/10">
          {IDD_MODULE.map((modul) => {
            const inhalt = iddInhaltVon(data, modul)
            if (!inhalt) return null
            return (
              <li key={`${modul.art}-${modul.key}`} className="flex flex-wrap items-center gap-4 px-4 py-3 sm:px-5">
                <img
                  src={versionedImageUrl(inhalt.bild)}
                  alt=""
                  className="h-12 w-16 shrink-0 rounded object-cover"
                />
                <div className="min-w-0 flex-1">
                  <p className="text-[14px] font-medium">{modul.titel}</p>
                  <p className="text-[12.5px] text-muted-foreground">
                    {IDD_ART_TITEL[modul.art]}
                    {inhalt.geaendertAm && ` · geändert am ${formatDatum(inhalt.geaendertAm)}`}
                  </p>
                </div>
                {inhalt.angepasst ? (
                  <Badge variant="outline" className="font-normal">Angepasst</Badge>
                ) : (
                  <Badge variant="secondary" className="font-normal">Standard</Badge>
                )}
                <div className="flex flex-wrap gap-2">
                  <Button asChild variant="ghost" size="sm">
                    <Link to="/idd/modul/$art/$key" params={{ art: modul.art, key: modul.key }}>
                      Ansehen
                    </Link>
                  </Button>
                  {inhalt.angepasst && (
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={zuruecksetzen.isPending}
                      onClick={() => void standard(inhalt)}
                    >
                      Standard wiederherstellen
                    </Button>
                  )}
                  <Button variant="outline" size="sm" onClick={() => setOffen({ titel: modul.titel, inhalt })}>
                    Bearbeiten
                  </Button>
                </div>
              </li>
            )
          })}
        </ul>
      )}
      {offen && (
        <IddInhaltDialog
          key={`${offen.inhalt.art}-${offen.inhalt.modulKey}`}
          titel={offen.titel}
          inhalt={offen.inhalt}
          onClose={() => setOffen(undefined)}
        />
      )}
    </div>
  )
}

export const Route = createFileRoute("/_app/admin/idd-inhalte")({
  component: AdminIddInhalte,
})
